import { useEffect, useState } from 'react';
import {
  Alert,
  AttachmentField,
  Badge,
  Button,
  DestinationGroup,
  Field,
  Icon,
  Modal,
  ModalActions,
  SearchSelect,
  TextArea,
  type IconName,
} from '@evogard/ui';
import type { AnalysisForm, ClaimContactAttempt } from '@evogard/contract';
import { analysisHoldLabel, formatInstant, formatPhone } from '@evogard/domain';
import {
  ActivityRecordButtons,
  HoldDialog,
} from '../../../components/activity/ActivityRecords.js';
import type { Desk } from './desk.js';
import { claimFileUrl, useDeskMutations } from './useDesk.js';

type Channel = 'ligacao' | 'whatsapp' | 'email';
const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'ligacao', label: 'Ligação' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'E-mail' },
];

/** O que cada registro da Atividade é, no botão e no modal dele. */
const RECORDS: {
  code: 'contato' | 'pendencia' | 'juridico';
  icon: IconName;
  title: string;
  /** Some o registro quando o sinistro já está parado: não se para duas vezes. */
  stops: boolean;
}[] = [
  { code: 'contato', icon: 'demandas', title: 'Tentativa de contato', stops: false },
  { code: 'pendencia', icon: 'pausa', title: 'Pendência', stops: true },
  /* O jurídico (30/10/2026): para como a pendência, até o módulo do Jurídico existir. */
  { code: 'juridico', icon: 'livro', title: 'Jurídico', stops: true },
];

/**
 * Onde o sinistro está DENTRO da etapa, e o que se registra sem mudar a etapa
 * — decisão do dono em 06/10/2026: fase é só onde se trabalha.
 *
 * - **A situação** (`claims_phase_passages.status`): na Entrada, "aguardando
 *   retorno" quando a última tentativa ficou sem resposta; na Documentação,
 *   pelos documentos que chegaram; na Negativa, o status do envio da carta.
 * - **A parada** (`hold_type`): pendência ou pedido de desistência. Parado, o
 *   sinistro não avança nem encerra — o movimento à direita fica vazio, a não
 *   ser a Desistência que confirma o pedido. "Retomar" o devolve à etapa.
 *
 * Três peças, em dois lugares (08/10/2026):
 *
 * - `HoldNotice` — a situação, a parada e o "Retomar", no alto do movimento;
 * - `ActivityRecords` — a tentativa de contato e a pendência, na aba
 *   Atividade, que é onde o registro aparece;
 * - `WithdrawalRequest` — o pedido de desistência, no movimento, com o mesmo
 *   botão dos destinos: ele vai ser uma fase geral do sinistro, e deixou de
 *   ser apresentado como registro. Enquanto a fase não existe, o efeito é o
 *   de sempre — o sinistro para na etapa com o pedido.
 *
 * O formulário de cada um abre num modal pequeno por cima da mesa (pedido do
 * dono, 06/10/2026). Cada um entra na Atividade; nenhum tira o sinistro da
 * etapa.
 */
interface RecordsProps {
  desk: Desk;
  claimId: number;
  /** A fase onde o sinistro está — a de onde o registro parte. */
  phase: string;
  form: AnalysisForm | undefined;
  /** Pode registrar: tem a permissão e o sinistro não está arquivado. */
  enabled: boolean;
}

export function HoldNotice({ desk, claimId, phase, form, enabled }: RecordsProps) {
  const mutations = useDeskMutations(desk, claimId);
  const [error, setError] = useState<string | null>(null);
  const [opinion, setOpinion] = useState('');
  const hold = form?.hold ?? null;
  const situation = form?.situation ?? null;
  /* O tipo da pendência, onde o departamento os tem (Indenização, I2). */
  const kindLabel =
    hold?.pendencyKind && desk.pendencyKinds
      ? (desk.pendencyKinds[hold.pendencyKind] ?? hold.pendencyKind)
      : null;
  /* Na Indenização, retomar a pendência leva o parecer (§5.9). */
  const asksOpinion = hold?.type === 'pendencia' && desk.pendencyKinds !== undefined;
  if (!situation && !hold && !error) return null;

  return (
    <div className="flex flex-col gap-3">
      {(situation || hold) && (
        <div className="flex flex-wrap items-center gap-2">
          {situation && <Badge tone="progresso">{desk.situationLabel(situation)}</Badge>}
          {hold && (
            <Badge tone="alerta">
              Parado: {analysisHoldLabel(hold.type)}
              {kindLabel ? ` · ${kindLabel}` : ''}
            </Badge>
          )}
        </div>
      )}

      {hold && (
        <Alert
          tone="alerta"
          title={`Parado por ${analysisHoldLabel(hold.type).toLowerCase()}${kindLabel ? ` ${kindLabel.toLowerCase()}` : ''}`}
        >
          {hold.text && <p>{hold.text}</p>}
          <p className="mt-1 text-xs">
            Desde {formatInstant(hold.since)}. O sinistro continua nesta etapa
            {hold.type === 'pre_desistencia'
              ? ': confirme a desistência abaixo, ou retome se o cliente voltou atrás.'
              : hold.type === 'negado_com_posse'
                ? ', com o veículo na base: quando o associado o buscar, conclua abaixo. Voltar continua livre.'
                : ', e não avança até ser retomado.'}
          </p>
          {enabled && (
            <div className="mt-2 flex flex-col gap-2">
              {asksOpinion && (
                <TextArea
                  label="Parecer"
                  value={opinion}
                  onChange={(e) => setOpinion(e.target.value)}
                  maxLength={4000}
                  rows={2}
                  help="Como a pendência foi resolvida. Fica na pendência, ao retomar."
                />
              )}
              <div>
                <Button
                  variant="contorno"
                  size="sm"
                  loading={mutations.resume.isPending}
                  onClick={() =>
                    mutations.resume.mutate(
                      {
                        fromPhase: phase,
                        ...(asksOpinion && opinion.trim() !== ''
                          ? { note: opinion.trim() }
                          : {}),
                      },
                      {
                        onError: (e) => setError(e.message),
                        onSuccess: () => {
                          setError(null);
                          setOpinion('');
                        },
                      },
                    )
                  }
                >
                  Retomar
                </Button>
              </div>
            </div>
          )}
        </Alert>
      )}

      {error && <Alert tone="erro">{error}</Alert>}
    </div>
  );
}

/**
 * Os botões "Registrar na Atividade" do sinistro e de Periféricos: a tentativa
 * de contato e a pendência. A peça é a de `components/activity/` desde
 * 28/10/2026, quando o Financeiro passou a registrar a pendência do mesmo jeito.
 */
export function RecordButtons(props: {
  held: boolean;
  heldTitle: string;
  onOpen: (record: 'contato' | 'pendencia' | 'juridico') => void;
}) {
  return <ActivityRecordButtons records={RECORDS} {...props} />;
}

export { HoldDialog };

/** A tentativa de contato e a pendência, no alto da aba Atividade. */
export function ActivityRecords({ desk, claimId, phase, form, enabled }: RecordsProps) {
  const mutations = useDeskMutations(desk, claimId);
  const [open, setOpen] = useState<'contato' | 'pendencia' | 'juridico' | null>(null);
  const hold = form?.hold ?? null;
  if (!enabled || desk.isOutcome(phase)) return null;

  return (
    <>
      <RecordButtons
        held={hold !== null}
        heldTitle="O sinistro já está parado: retome antes."
        onOpen={setOpen}
      />

      {open === 'contato' && (
        <ContactDialog
          effect={contactEffect(desk, phase)}
          phase={phase}
          suggestions={
            form?.contactSuggestions ?? { phones: [], emails: [], workshop: null }
          }
          submit={(input) => mutations.registerContact.mutateAsync(input)}
          onClose={() => setOpen(null)}
        />
      )}
      {(open === 'pendencia' || open === 'juridico') && (
        <HoldModal
          desk={desk}
          claimId={claimId}
          phase={phase}
          type={open}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/**
 * O pedido de desistência, no movimento, com o botão dos destinos. Some com o
 * sinistro já parado — o aviso do alto diz por quê e oferece o "Retomar".
 */
export function WithdrawalRequest({ desk, claimId, phase, form, enabled }: RecordsProps) {
  const [open, setOpen] = useState(false);
  if (!enabled || desk.isOutcome(phase) || form?.hold) return null;

  return (
    <>
      <DestinationGroup
        title="Fase geral do sinistro"
        direction="forward"
        destinations={[{ id: 'pre_desistencia', name: 'Pedido de desistência' }]}
        selected={null}
        onSelect={() => setOpen(true)}
      />
      {open && (
        <HoldModal
          desk={desk}
          claimId={claimId}
          phase={phase}
          type="pre_desistencia"
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** O que a tentativa de contato manda — o corpo da rota, com o print ainda em `File`. */
export interface ContactAttemptInput {
  fromPhase: string;
  channel: Channel;
  answered: boolean;
  phone?: string;
  email?: string;
  protocol?: string;
  companyId?: number;
  /** A evidência do contato — obrigatória desde 21/10/2026. */
  print: { file: File; typeMime: string };
  notes?: string;
}

/**
 * A tentativa de contato num modal, a partir da aba Atividade. O miolo é o
 * `ContactAttemptForm`, o mesmo que o formulário da fase abre sem modal. Quem
 * grava é `submit` — a rota do sinistro ou a do acionamento de Periféricos.
 */
export function ContactDialog({
  effect,
  phase,
  suggestions,
  submit,
  onClose,
}: {
  /** O que a tentativa faz com o card, dito antes de registrar. */
  effect: string;
  phase: string;
  suggestions: AnalysisForm['contactSuggestions'];
  submit: (input: ContactAttemptInput) => Promise<unknown>;
  onClose: () => void;
}) {
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Tentativa de contato"
      descricao={effect}
      icon="demandas"
      tone="destaque"
      size="sm"
    >
      <ContactAttemptForm
        phase={phase}
        suggestions={suggestions}
        submit={submit}
        onDone={onClose}
        onCancel={onClose}
      />
    </Modal>
  );
}

/** O que a tentativa faz com o sinistro, dito antes de registrar. */
const contactEffect = (desk: Desk, phase: string) =>
  phase === 'entrada'
    ? 'Fica na Atividade. Sem resposta, a Entrada passa a "aguardando retorno"; respondida, sai dessa situação.'
    : `Fica na Atividade, e o sinistro continua em ${desk.phaseOf(phase)?.label ?? phase}.`;

/**
 * A tentativa de contato: o canal, para onde ela foi, uma observação se houver,
 * o print se houver, e a resposta — que é o botão. Na Entrada, sem resposta
 * põe o sinistro em "aguardando retorno"; respondida, tira.
 *
 * **Para onde ela foi é obrigatório** (decisão do dono em 06/10/2026): o
 * número na ligação e no WhatsApp; o protocolo do atendimento no WhatsApp; o
 * e-mail no e-mail. Não há "chat": é o mesmo atendimento do WhatsApp. O número e o e-mail abrem com o do
 * cadastro de quem é o sinistro, e os outros que o cadastro tem ficam a um
 * clique. Os botões não ficam desabilitados por falta de campo: clicar marca o
 * que falta (regra 1 dos formulários).
 *
 * **A evidência é obrigatória** (21/10/2026; opcional desde 15/10): no lugar
 * do campo "Print do chat ou tentativa de ligação" que a Entrada tinha, e
 * presa a esta tentativa.
 *
 * **No Reparo, a tentativa pode ser com a oficina** (07/10/2026): com a oficina
 * do reparo escolhida, ela vem primeiro, e a tentativa grava a empresa junto.
 */
function ContactAttemptForm({
  phase,
  suggestions,
  submit,
  onDone,
  onCancel,
}: {
  phase: string;
  suggestions: AnalysisForm['contactSuggestions'];
  submit: (input: ContactAttemptInput) => Promise<unknown>;
  onDone: () => void;
  /** Sem ela, não há "Cancelar" — o formulário da fase o mostra aberto. */
  onCancel?: () => void;
}) {
  const workshop = suggestions.workshop;
  /** Com quem: a oficina do reparo, ou quem é o sinistro (associado ou terceiro). */
  const [target, setTarget] = useState<'oficina' | 'cliente'>(
    workshop ? 'oficina' : 'cliente',
  );
  const withWorkshop = target === 'oficina' && workshop !== null;
  const [channel, setChannel] = useState<Channel>('ligacao');
  const [phone, setPhone] = useState(() => formatPhone(suggestions.phones[0] ?? ''));
  const [email, setEmail] = useState(suggestions.emails[0] ?? '');
  const [protocol, setProtocol] = useState('');
  const [notes, setNotes] = useState('');
  const [print, setPrint] = useState<{
    file: File;
    typeMime: string;
    url: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  /** Qual das duas respostas está gravando — só ela gira. */
  const [sending, setSending] = useState<boolean | null>(null);

  /* A prévia do print é uma `blob:` URL — solta quando ele sai ou o formulário fecha. */
  useEffect(
    () => () => {
      if (print) URL.revokeObjectURL(print.url);
    },
    [print],
  );

  const byEmail = channel === 'email';
  const needsProtocol = channel === 'whatsapp';
  const missing = {
    phone: !byEmail && phone.trim() === '',
    email: byEmail && email.trim() === '',
    protocol: needsProtocol && protocol.trim() === '',
    print: print === null,
  };
  const others = byEmail
    ? suggestions.emails.filter((e) => e !== email.trim())
    : suggestions.phones.filter((p) => formatPhone(p) !== phone.trim());

  const register = (answered: boolean) => {
    setTried(true);
    setError(null);
    if (missing.phone || missing.email || missing.protocol || missing.print) return;
    setSending(answered);
    submit({
      fromPhase: phase,
      channel,
      answered,
      ...(byEmail ? { email: email.trim() } : { phone: phone.trim() }),
      ...(needsProtocol ? { protocol: protocol.trim() } : {}),
      ...(withWorkshop ? { companyId: workshop.id } : {}),
      print: { file: print!.file, typeMime: print!.typeMime },
      notes: notes.trim() || undefined,
    })
      .then(onDone, (e: Error) => setError(e.message))
      .finally(() => setSending(null));
  };
  const required = (empty: boolean) => (tried && empty ? 'Obrigatório.' : null);

  return (
    <div className="flex flex-col gap-4">
      {workshop && (
        <SearchSelect
          label="Com quem"
          options={[
            { value: 'oficina', label: `A oficina — ${workshop.name}` },
            { value: 'cliente', label: 'O associado ou o terceiro' },
          ]}
          value={target}
          onChange={(v) => {
            const next = (v || 'cliente') as 'oficina' | 'cliente';
            setTarget(next);
            /* O número e o e-mail acompanham: os da oficina, ou os do cadastro. */
            const own = next === 'oficina';
            setPhone(
              formatPhone(
                (own
                  ? workshop.phone
                  : suggestions.phones.find((p) => p !== workshop.phone)) ?? '',
              ),
            );
            setEmail(
              (own
                ? workshop.email
                : suggestions.emails.find((e) => e !== workshop.email)) ?? '',
            );
          }}
        />
      )}
      <SearchSelect
        label="Canal"
        options={CHANNELS}
        value={channel}
        onChange={(v) => setChannel((v || 'ligacao') as Channel)}
      />
      {byEmail ? (
        <Field
          label="E-mail"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={required(missing.email)}
          help={
            suggestions.emails.length === 0 ? 'O cadastro não tem e-mail.' : undefined
          }
        />
      ) : (
        <Field
          label="Número tentado"
          inputMode="tel"
          required
          placeholder="(85) 99999-0000"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={required(missing.phone)}
          help={
            suggestions.phones.length === 0 ? 'O cadastro não tem telefone.' : undefined
          }
        />
      )}
      {others.length > 0 && (
        <div className="-mt-2 flex flex-wrap items-center gap-1.5 text-xs text-texto-suave">
          <span>Também no cadastro:</span>
          {others.map((value) => (
            <Button
              key={value}
              size="sm"
              variant="discreto"
              onClick={() => (byEmail ? setEmail(value) : setPhone(formatPhone(value)))}
            >
              {byEmail ? value : formatPhone(value)}
            </Button>
          ))}
        </div>
      )}
      {needsProtocol && (
        <Field
          label="Protocolo do atendimento"
          required
          value={protocol}
          onChange={(e) => setProtocol(e.target.value)}
          error={required(missing.protocol)}
        />
      )}
      <TextArea
        label="Observação"
        help="Opcional: o que foi dito, o horário combinado para retornar."
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={2000}
        rows={2}
      />
      <div className="flex flex-col gap-1.5">
        <AttachmentField
          label={
            <>
              Evidência de contato<span className="text-erro"> *</span>
            </>
          }
          max={1}
          items={
            print
              ? [
                  {
                    key: 'print',
                    nome: print.file.name,
                    typeMime: print.typeMime,
                    sizeBytes: print.file.size,
                    url: print.url,
                  },
                ]
              : []
          }
          onAdd={(files, types) => {
            const file = files[0];
            if (file) {
              setPrint({ file, typeMime: types[0]!, url: URL.createObjectURL(file) });
            }
          }}
          onRemove={() => setPrint(null)}
          disabled={sending !== null}
        />
        {required(missing.print) && (
          <p className="text-rotulo text-erro">{required(missing.print)}</p>
        )}
      </div>
      {error && <Alert tone="erro">{error}</Alert>}
      <ModalActions>
        {onCancel && (
          <Button variant="secundario" disabled={sending !== null} onClick={onCancel}>
            Cancelar
          </Button>
        )}
        <Button
          variant="contorno"
          loading={sending === false}
          disabled={sending !== null}
          onClick={() => register(false)}
        >
          Sem resposta
        </Button>
        <Button
          loading={sending === true}
          disabled={sending !== null}
          onClick={() => register(true)}
        >
          Respondeu
        </Button>
      </ModalActions>
    </div>
  );
}

/**
 * As tentativas de contato da fase, no topo do formulário dela (15/10/2026) —
 * no lugar do número "Tentativas de contato". A Entrada exige ao menos uma.
 * É o mesmo botão da aba Atividade, e abre o mesmo modal; embaixo, uma linha
 * por tentativa já feita, com a evidência a um clique.
 */
export function ContactAttempts({
  desk,
  claimId,
  phase,
  form,
  required,
  error,
  editable,
}: {
  desk: Desk;
  claimId: number;
  phase: string;
  form: AnalysisForm;
  required: boolean;
  error: string | null;
  /** Registra daqui: é a fase onde o sinistro está, e a pessoa pode. */
  editable: boolean;
}) {
  const mutations = useDeskMutations(desk, claimId);
  const contacts = form.contacts;
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        disabled={!editable}
        onClick={() => setOpen(true)}
        className={`flex min-h-11 items-center gap-2.5 rounded-borda border bg-superficie px-3 py-2 text-left text-sm font-semibold text-texto enabled:hover:border-borda-forte disabled:cursor-not-allowed disabled:opacity-55 ${error ? 'border-erro' : 'border-borda'}`}
      >
        <Icon nome="demandas" size={16} className="shrink-0 text-texto-suave" />
        <span className="min-w-0 flex-1">
          Tentativa de contato
          {required && <span className="text-erro"> *</span>}
        </span>
        <span className="rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
          {contacts.length}
        </span>
        {editable && <Icon nome="mais" size={16} className="shrink-0 text-texto-tenue" />}
      </button>
      {contacts.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-xs text-texto-suave">
          {contacts.map((c) => (
            <ContactAttemptItem key={c.id} claimId={claimId} contact={c} />
          ))}
        </ul>
      )}
      {error && <p className="text-rotulo text-erro">{error}</p>}
      {open && (
        <ContactDialog
          effect={contactEffect(desk, phase)}
          phase={phase}
          suggestions={form.contactSuggestions}
          submit={(input) => mutations.registerContact.mutateAsync(input)}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/** Uma tentativa, numa linha: a resposta, por onde, para onde, quando e a evidência. */
function ContactAttemptItem({
  claimId,
  contact: c,
}: {
  claimId: number;
  contact: ClaimContactAttempt;
}) {
  const channel = CHANNELS.find((o) => o.value === c.channel)?.label ?? c.channel;
  const to = c.email ?? (c.phone ? formatPhone(c.phone) : null);
  const open = async (documentId: number) => {
    window.open(await claimFileUrl(claimId, documentId), '_blank', 'noopener');
  };
  return (
    <li className="flex flex-wrap items-center gap-x-1.5">
      <span
        className={
          c.answered ? 'font-semibold text-sucesso' : 'font-semibold text-alerta'
        }
      >
        {c.answered ? 'Respondeu' : 'Sem resposta'}
      </span>
      <span>·</span>
      <span>{[channel, c.companyName, to].filter(Boolean).join(' · ')}</span>
      <span>·</span>
      <span>{formatInstant(c.happenedAt)}</span>
      {c.files.map((d) => (
        <Button key={d.id} size="sm" variant="discreto" onClick={() => void open(d.id)}>
          Evidência
        </Button>
      ))}
    </li>
  );
}

/** A pendência, o jurídico ou o pedido de desistência: o texto, e parar o sinistro na etapa. */
function HoldModal({
  desk,
  claimId,
  phase,
  type,
  onClose,
}: {
  desk: Desk;
  claimId: number;
  phase: string;
  type: 'pendencia' | 'pre_desistencia' | 'juridico';
  onClose: () => void;
}) {
  const mutations = useDeskMutations(desk, claimId);
  const phaseName = desk.phaseOf(phase)?.label ?? phase;
  return (
    <HoldDialog
      type={type}
      description={
        type === 'pendencia' || type === 'juridico'
          ? `O sinistro fica parado em ${phaseName} até alguém retomá-lo.`
          : `O sinistro fica parado em ${phaseName} até o cliente confirmar a desistência ou voltar atrás.`
      }
      /* A pendência com tipo — a Indenização (I2, 09/10/2026): o tipo é obrigatório. */
      kinds={type === 'pendencia' ? desk.pendencyKinds : undefined}
      submit={(input) => mutations.hold.mutateAsync({ fromPhase: phase, type, ...input })}
      onClose={onClose}
    />
  );
}
